import React, { useEffect } from 'react';

/**
 * ConfirmDialog — Modal de confirmação reutilizável
 *
 * Props:
 *   open       - boolean: controla visibilidade
 *   icon       - React element (opcional): ícone exibido acima do título
 *   title      - string: título do modal
 *   message    - string: mensagem/descrição
 *   onConfirm  - function: callback ao confirmar
 *   onCancel   - function: callback ao cancelar / fechar
 *   confirmLabel - string (default: 'Confirmar')
 *   cancelLabel  - string (default: 'Cancelar')
 *   danger     - boolean: se true, botão fica vermelho
 */
const ConfirmDialog = ({
  open,
  icon,
  title,
  message,
  onConfirm,
  onCancel,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  danger = false,
}) => {
  // Fechar com ESC
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onCancel?.();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onCancel]);

  // Bloquear scroll do body
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        padding: '16px',
        animation: 'confirmFadeIn 0.15s ease-out',
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: 'var(--dp-bg-card, #1a1a2e)',
          border: '1px solid var(--dp-border-medium, rgba(255,255,255,0.1))',
          borderRadius: '16px',
          padding: '28px',
          maxWidth: 420,
          width: '100%',
          animation: 'confirmSlideIn 0.2s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        {icon && (
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px',
              background: danger
                ? 'rgba(239, 68, 68, 0.12)'
                : 'rgba(59, 130, 246, 0.12)',
            }}
          >
            {React.cloneElement(icon, {
              size: icon.props.size || 22,
              style: {
                color: danger
                  ? '#EF4444'
                  : 'var(--dp-blue-500, #0DAEFF)',
                ...(icon.props.style || {}),
              },
            })}
          </div>
        )}

        {/* Title */}
        <h3
          style={{
            margin: '0 0 8px',
            fontSize: '16px',
            fontWeight: 600,
            color: 'var(--dp-text-primary, #fff)',
          }}
        >
          {title}
        </h3>

        {/* Message */}
        <p
          style={{
            margin: '0 0 24px',
            fontSize: '14px',
            color: 'var(--dp-text-secondary, #999)',
            lineHeight: 1.6,
          }}
        >
          {message}
        </p>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '9px 20px',
              borderRadius: '10px',
              background: 'transparent',
              border: '1px solid var(--dp-border-medium, rgba(255,255,255,0.1))',
              color: 'var(--dp-text-secondary, #999)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255,255,255,0.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '9px 20px',
              borderRadius: '10px',
              background: danger
                ? '#EF4444'
                : 'linear-gradient(135deg, var(--dp-blue-500, #0DAEFF), #3B82F6)',
              border: 'none',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.opacity = '0.85';
            }}
            onMouseLeave={(e) => {
              e.target.style.opacity = '1';
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes confirmFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes confirmSlideIn {
          from { opacity: 0; transform: scale(0.95) translateY(8px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ConfirmDialog;
